import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { defineMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';

import { Sport, StoryState } from '__generated__/globalTypes';
import { Dropdown } from 'atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from 'atoms/icons';
import { Binoculars } from 'atoms/icons/Binoculars';
import { HomeIcon } from 'atoms/icons/HomeIcon';
import { Market } from 'atoms/icons/Market';
import { More } from 'atoms/icons/More';
import { MyCards } from 'atoms/icons/MyCards';
import { PlayFootball } from 'atoms/icons/PlayFootball';
import { PlayMLB } from 'atoms/icons/PlayMLB';
import { PlayNBA } from 'atoms/icons/PlayNBA';
import { Horizontal } from 'atoms/layout/flex';
import {
  ANY_SPORT_HOME,
  ANY_SPORT_MARKET,
  ANY_SPORT_MY_CARDS,
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_QUICK_PLAY,
  ANY_SPORT_SCOUTING_CENTER,
} from 'constants/__generated__/routes';
import { useConfigContext } from 'contexts/config';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useSimplifiedNav } from 'hooks/commonGame/useSimplifiedNav';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useLifecycle } from 'hooks/useLifecycle';
import { useUnclaimedMarketTasksCount } from 'hooks/users/useUnclaimedMarketTasksCount';
import { useEvents } from 'lib/events/useEvents';
import { navLabels, scoutingCenter } from 'lib/glossary';
import {
  generateSportPath,
  matchAnySportPath,
} from 'lib/routing/generateSportPath';
import { laptopAndAbove } from 'style/mediaQuery';

import { MoreNavItems } from './MoreNavItems';
import { SearchNavItem } from './SearchNavItem';
import { NavItem } from '../../NavItem';

const Root = styled(Horizontal).attrs({
  gap: 0,
})`
  width: 100%;
  position: relative;
  justify-content: space-between;

  & > * {
    flex: 1;
  }
  @media ${laptopAndAbove} {
    flex-direction: column;
    align-items: stretch;
  }
`;
const DropdownContent = styled(Horizontal)`
  background-color: var(--c-black);
  padding: var(--half-unit) var(--half-unit) var(--intermediate-unit)
    var(--half-unit);
`;

const ICONS = {
  [Sport.BASEBALL]: PlayMLB,
  [Sport.FOOTBALL]: PlayFootball,
  [Sport.NBA]: PlayNBA,
} as const;

const itemsToClaim = defineMessage({
  id: 'NavItems.playItemsToClaim',
  defaultMessage: '{count, plural, one {# item} other {# items}} to claim',
});

type Props = { sport: Sport; sidebarCollapsed?: boolean };
export const NavItems = ({ sport, sidebarCollapsed }: Props) => {
  const { formatMessage } = useIntl();
  const track = useEvents();
  const location = useLocation();
  const isLaptopAndAbove = useIsLaptop();
  const navItemsVariant = isLaptopAndAbove ? 'horizontal' : 'vertical';
  const { lifecycle } = useLifecycle();
  const { showSimplifiedNav } = useSimplifiedNav(sport);
  const { so5 } = useConfigContext();
  const { currentUser } = useCurrentUserContext();

  const PlayIcon = ICONS[sport];

  const showLiveDot = so5?.currentManagerHasLiveGames;

  const myCardsRewardsCount = currentUser?.unclaimedSo5Rewards?.length || 0;

  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();

  const playBadgeCount = {
    [Sport.FOOTBALL]:
      so5?.myAvailableFixtureStory?.aasmState === StoryState.READY ? 1 : 0,
    [Sport.BASEBALL]: 0,
    [Sport.NBA]: 0,
  }[sport];

  const marketBadgeCount = {
    [Sport.FOOTBALL]: unclaimedMarketTasksCount,
    [Sport.BASEBALL]: 0,
    [Sport.NBA]: unclaimedMarketTasksCount,
  }[sport];

  return (
    <Root>
      {!!isLaptopAndAbove && !!sidebarCollapsed && (
        <SearchNavItem
          variant={navItemsVariant}
          icon={() => <FontAwesomeIcon icon={faSearch} size="sm" />}
          label={formatMessage(navLabels.search)}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}

      <NavItem
        variant={navItemsVariant}
        icon={() => <HomeIcon size={isLaptopAndAbove ? 2 : 2.5} />}
        label={formatMessage(navLabels.home)}
        to={generateSportPath(ANY_SPORT_HOME, { sport })}
        sidebarCollapsed={sidebarCollapsed}
        badge={{
          content: currentUser?.myUnclaimedTasksCount,
        }}
        enableContextModeNavigation
      />
      <NavItem
        variant={navItemsVariant}
        icon={({ isActive }) => (
          <PlayIcon size={isLaptopAndAbove ? 2 : 2.5} active={isActive} />
        )}
        label={formatMessage(navLabels.play)}
        to={generateSportPath(
          lifecycle?.lastVisitedPlayTab?.[sport] === 'quickPlay' &&
            sport === Sport.NBA
            ? ANY_SPORT_PLAY_QUICK_PLAY
            : ANY_SPORT_PLAY,
          {
            sport,
          }
        )}
        showLiveDot={sport !== Sport.NBA && showLiveDot}
        badge={playBadgeCount > 0 ? { content: playBadgeCount } : undefined}
        sidebarCollapsed={sidebarCollapsed}
        enableContextModeNavigation
      />
      {!showSimplifiedNav && (
        <NavItem
          variant={navItemsVariant}
          icon={() => <Binoculars size={isLaptopAndAbove ? 2 : 2.5} />}
          label={formatMessage(scoutingCenter.scout)}
          to={generateSportPath(ANY_SPORT_SCOUTING_CENTER, {
            sport,
          })}
          onClick={() =>
            track('Click Scout Nav Item', {
              sport,
            })
          }
          sidebarCollapsed={sidebarCollapsed}
          enableContextModeNavigation
        />
      )}
      <NavItem
        variant={navItemsVariant}
        icon={({ isActive }) => (
          <Market size={isLaptopAndAbove ? 2 : 2.5} active={isActive} />
        )}
        label={formatMessage(navLabels.transferMarket)}
        to={generateSportPath(ANY_SPORT_MARKET, {
          sport,
        })}
        sidebarCollapsed={sidebarCollapsed}
        badge={{
          content: marketBadgeCount,
        }}
        enableContextModeNavigation
      />
      <NavItem
        variant={navItemsVariant}
        icon={({ isActive }) => (
          <MyCards size={isLaptopAndAbove ? 2 : 2.5} active={isActive} />
        )}
        label={formatMessage(navLabels.myCards)}
        to={generateSportPath(ANY_SPORT_MY_CARDS, {
          sport,
        })}
        badge={{
          content: myCardsRewardsCount,
          ariaLabel: formatMessage(itemsToClaim, {
            count: myCardsRewardsCount,
          }),
        }}
        keepSearchParams={
          !!matchAnySportPath(catchAll(ANY_SPORT_MY_CARDS), location.pathname)
        }
        sidebarCollapsed={sidebarCollapsed}
        enableContextModeNavigation
      />
      {!!isLaptopAndAbove && (
        <Dropdown
          forceDesktopBehavior
          side="right"
          gap={24}
          label={({ onClick }) => (
            <button onClick={onClick} type="button">
              <NavItem
                icon={() => <More size={2} />}
                label={formatMessage(navLabels.more)}
                sidebarCollapsed={sidebarCollapsed}
              />
            </button>
          )}
        >
          {({ closeDropdown }) => (
            <DropdownContent>
              <MoreNavItems onClick={closeDropdown} />
            </DropdownContent>
          )}
        </Dropdown>
      )}
    </Root>
  );
};
